import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';

import ThemeProvider from 'hb-react/style/ThemeProvider';

export const purposes = ['primary', 'secondary', 'danger'];
export const sizes = ['small', 'medium', 'large'];

const ButtonGroup = ({ children, ...props }) => (
  <Group {...props}>{children}</Group>
);

ButtonGroup.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * @deprecated please use `@hivebrite/design-system/Button` instead
 */
const Button = ({
  as,
  children,
  dataTestId,
  disabled,
  inverted,
  onClick,
  purpose,
  type,
  size,
  ...props
}) => (
  <ThemeProvider theme="bo">
    <StyledButton
      as={as}
      data-testid={dataTestId}
      disabled={disabled}
      display="inline-block"
      inverted={inverted}
      purpose={purpose}
      size={size}
      type={type}
      {...(!disabled && { onClick })}
      {...props}
    >
      {children}
    </StyledButton>
  </ThemeProvider>
);

Button.propTypes = {
  as: PropTypes.string,
  children: PropTypes.any.isRequired,
  dataTestId: PropTypes.string,
  disabled: PropTypes.bool,
  inverted: PropTypes.bool,
  onClick: PropTypes.func,
  purpose: PropTypes.oneOf(purposes),
  size: PropTypes.oneOf(sizes),
  type: PropTypes.string,
};

Button.defaultProps = {
  as: 'button',
  purpose: 'secondary',
  size: 'medium',
};

Button.Group = ButtonGroup;

export default Button;

const disabledStyle = ({ disabled }) =>
  disabled
    ? css`
        opacity: 0.5;
      `
    : css`
        &:hover,
        &:focus {
          cursor: pointer;
          text-decoration: none;
        }
      `;

const purposeStyle = ({ inverted, disabled, theme, purpose }) => {
  const purposes = {
    primary: css`
      background-color: ${inverted ? theme.colors.white : theme.colors.primary};
      color: ${inverted ? theme.colors.primary : theme.colors.white};

      ${!disabled &&
      css`
        &:hover,
        &:focus {
          color: ${theme.colors.white};
          background-color: ${inverted
            ? theme.colors.primary
            : theme.colors.primaryDark};
        }
      `}

      ${inverted &&
      css`
        border: 1px solid ${theme.colors.primary};
      `}
    `,

    secondary: css`
      background-color: ${inverted ? theme.colors.white : '#EBEEF4'};
      color: #050505;

      ${!disabled &&
      css`
        &:hover,
        &:focus {
          background-color: ${inverted ? '#e3eaef' : '#0434f9'};
          color: ${inverted ? '#313a46' : theme.colors.white};
        }
      `}

      ${inverted &&
      css`
        border: 1px solid #e3eaef;
      `}
    `,

    danger: css`
      background-color: ${inverted ? theme.colors.white : '#fd4329'};
      color: ${inverted ? '#fd4329' : theme.colors.white};

      ${!disabled &&
      css`
        &:hover,
        &:focus {
          color: ${theme.colors.white};
          background-color: ${inverted ? '#fd4329' : '#ee395b'};
        }
      `}

      ${inverted &&
      css`
        border: 1px solid #fd4329;
      `}
    `,
  };

  return purposes[purpose];
};

const sizeStyle = ({ size, theme, ...props }) => {
  const sizes = {
    small: css`
      ${!props.px && `padding: 0 ${theme.space[4]}px;`}
      line-height: ${theme.lineHeights[7]} !important;
      font-size: ${theme.fontSizes[0]};
    `,
    medium: css`
      ${!props.px && `padding: 0 14.4px;`}
      line-height: 36px !important;
      font-size: ${theme.fontSizes[2]};
    `,
    large: css`
      ${!props.px && `padding: 0 ${theme.space[10]}px;`}
      line-height: 55px !important;
      font-size: ${theme.fontSizes[1]};
    `,
  };

  return sizes[size];
};

const StyledButton = styled(Box).withConfig({
  shouldForwardProp: (prop) =>
    !['disabled', 'purpose', 'size', 'inverted', 'value'].includes(prop),
})`
  border: none;
  overflow: visible;
  font: inherit;
  font-weight: inherit;
  font-size: inherit;
  line-height: inherit;
  text-transform: none;
  box-sizing: border-box;
  vertical-align: middle;
  text-align: center;
  border-radius: 5px;
  font-weight: 500;
  white-space: nowrap;
  transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out,
    border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  ${disabledStyle}
  ${purposeStyle}
  ${sizeStyle}
`;

const Group = styled(Box).attrs(() => ({
  display: 'inline-flex',
}))`
  vertical-align: middle;
  position: relative;

  > ${StyledButton} {
    border-radius: 0px;

    &:first-of-type {
      border-top-left-radius: 5px !important;
      border-bottom-left-radius: 5px !important;
      margin-right: 1px;
    }

    &:last-of-type {
      border-top-right-radius: 5px !important;
      border-bottom-right-radius: 5px !important;
    }
  }
`;
